import { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import InterviewStorePoolsApi from 'libs/api/interviewStorePools';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import { useApi } from 'hooks/useApi';
import { interviewsAdapter } from './interviewStorePools.adapter';
import CompanyCategories from 'libs/api/admin/companyCategories';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import moment from 'moment';
import { EyeOutlined } from '@ant-design/icons';
import Helpers from 'helpers';
import { getFilters } from '../../../../hooks/useShops';

const companyCategoriesApi = new CompanyCategories();
const api = new InterviewStorePoolsApi();

const useInterviewStorePools = (isMainPage = true, isEdit) => {
  let { id } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [typeInterview, setTypeInterview] = useState('');
  const filtersData = useRecoilValue(getFilters);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const {
    data,
    loading,
    handleDownloadExcel,
    onChangeTableFilter,
    onDelete,
    setLoading,
    setUpdateInProcess,
    updateInProcess,
    singleItem,
  } = useApi({
    resourceKey: 'interview_store_pools',
    singleResourceKey: 'interview_store_pool',
    api,
    isMainPage,
    adapter: interviewsAdapter,
  });

  const typesInterview = useMemo(
    () => ({
      type_interview_company: t('INSEGNE'),
      type_interview_clients: t('Clienti'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => { },
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => { },
      },
      {
        title: t('DATA INIZIO'),
        dataIndex: 'date_from',
        sorter: () => { },
        render: (data) => moment(data).format('DD MMMM YYYY'),
      },
      {
        title: t('DATA FINE'),
        dataIndex: 'date_to',
        sorter: () => { },
        render: (data) => moment(data).format('DD MMMM YYYY'),
      },
      {
        title: t('DESTINATARI'),
        dataIndex: 'type_interview',
        sorter: () => { },
        render: (data) => typesInterview[data],
      },
      {
        title: t('Insegne/clienti che hanno risposto'),
        dataIndex: 'interview_store_pool_answers_size',
        width: 300,
        sorter: () => { },
      },
      {
        title: '',
        dataIndex: 'id',
        align: 'right',
        // width: 250,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'left'}
            onClick={(e) => e.stopPropagation()}
          >
            {row.can_link_to_interviews && (
              <Col>
                <Link to={`${pathname}/${row.id}/new`}>
                  <Button icon={<EyeOutlined />}>{t('Apri')}</Button>
                </Link>
              </Col>
            )}
            {row.can_result && (
              <Col>
                <Link to={`${pathname}/${row.id}/results`}>
                  <Button type={'green'}>{t('Risultati')}</Button>
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update ||
            row.can_destroy) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read, typesInterview]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((item) => ({
          title: item.title,
          value: item.id,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'by_date_from',
        values: [],
        keys: ['by_date_from', 'by_date_to'],
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('DESTINATARI'),
        type: 'select',
        name: 'by_type_interview',
        values: [
          { value: 'type_interview_company', title: t('INSEGNE') },
          { value: 'type_interview_clients', title: t('Clienti') },
        ],
      },
    ],
    [t, shoppingCenters],
  );

  const handleCreateOrUpdateCategory = async (values) => {
    setUpdateInProcess(true);
    try {
      const data = {
        interview_store_pool: {
          ...values,
        },
      };
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname, 2));
      }
    } catch (e) {
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
          shopping_center: 'shopping_center_id',
          company_category_answers: 'company_category_answer_ids',
          company_category_questions: 'company_category_question_ids',
        }),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            for (let key in singleItem) {
              switch (key) {
                case 'date_to':
                case 'date_from': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'YYYY-MM-DD'),
                  });
                  break;
                }
                case 'shopping_center': {
                  fieldsValues.push({
                    name: 'shopping_center_id',
                    value: singleItem[key].id,
                  });
                  break;
                }
                case 'company_categories': {
                  fieldsValues.push({
                    name: 'company_category_ids',
                    value: singleItem[key].map((item) => item.id),
                  });
                  break;
                }
                case 'company_category_questions': {
                  fieldsValues.push({
                    name: 'company_category_question_ids',
                    value: singleItem[key].map((item) => item.id),
                  });
                  break;
                }
                case 'company_category_answers': {
                  fieldsValues.push({
                    name: 'company_category_answer_ids',
                    value: singleItem[key].map((item) => item.id),
                  });
                  break;
                }
                case 'interview_questions': {
                  fieldsValues.push({
                    name: 'interview_questions_attributes',
                    value: singleItem[key],
                  });
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            setTypeInterview(singleItem.type_interview);
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleItem]);

  const handleDownloadExcelSingle = async () => {
    setLoading(true);
    try {
      const response = await api.getExcelSingle(id);
      Helpers.downloadFile(response, 'file.xlsx');
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerQuestions = async (values) => {
    setUpdateInProcess(true);
    try {
      const data = {
        data: {
          interview_store_pool_answer: {
            interview_store_pool_id: id,
            ...values,
          },
        },
      };
      await api.answerQuestions(id, data);
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      UIHelper.openNotificationWithIcon(
        'error',
        e?.response?.data?.base || t('Something went wrong!'),
      );
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    handleDownloadExcel,
    loading,
    onChangeTableFilter,
    form,
    shoppingCenters,
    setTypeInterview,
    typeInterview,
    companyCategories: filtersData?.company_categories,
    handleCreateOrUpdateCategory,
    updateInProcess,
    singleItem,
    handleDownloadExcelSingle,
    onDelete,
    handleAnswerQuestions,
    typesInterview,
  };
};

export default useInterviewStorePools;
